import { Body, Button, Form, Spacer } from '@spritz-finance/ui'
import axios from 'axios'
import React, { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import styled from 'styled-components'
import { FEEDBACK_SUBMITTED } from '../../analytics/events'
import confusedface from '../../assets/icons/confused-face-emoji.png'
import magnify from '../../assets/icons/magnifying-glass-emoji.png'
import partyface from '../../assets/icons/party-face-emoji.png'
import evilmonkey from '../../assets/icons/see-no-evil-monkey.png'
import walletConnect from '../../assets/icons/wallet-connect.svg'
import metamaskLogo from '../../assets/icons/metamask.svg'
import { useAnalytics } from '../../context/useAnalytics'
import { FeedbackForm } from '../../context/useFeedback'
import { config } from '../../modules/app/config'

import { FadePresence } from '../FadePresence'
import { Column } from '../Layout/Column'
import { Row } from '../Layout/Row'
import { EmojiLogo } from '../EmojiLogo'

const FullWidthInputWrapper = styled.div`
    width: 100%;
`

const InlineButton = styled(Body.Med)`
    text-decoration: underline;
    &:hover {
        cursor: pointer;
    }
`

const ThankYou: React.VFC<{ handleClose: () => void }> = ({ handleClose }) => {
    return (
        <Column align="center">
            <EmojiLogo src={partyface} />
            <Spacer height={32} />
            <Body.SmBold>Thank you for your submission!</Body.SmBold>
            <Spacer height={48} />

            <Button.Text onClick={handleClose}>Close</Button.Text>
        </Column>
    )
}

const GenericFeedbackContent = () => {
    return (
        <Column align="center">
            <EmojiLogo src={magnify} />
            <Spacer height={16} />
            <Body.SmBold>Send your feedback</Body.SmBold>

            <Spacer height={16} />
            <Body.Med size={14}>
                Let us know what you think about Spritz! Want to request a new feature, network,
                bill-pay account, or country?{' '}
                <InlineButton size={14} onClick={() => window.open('https://spritz.canny.io/')}>
                    Submit your requests here.
                </InlineButton>
            </Body.Med>
            <Spacer height={32} />
            <FullWidthInputWrapper>
                <Form.Input
                    textarea
                    name="feedback_text"
                    placeholder="Type your feedback here"
                    validation={{ required: true }}
                />
                <div style={{ display: 'none' }}>
                    <Form.Input type="hidden" name="reason" value={location.pathname} />
                </div>
            </FullWidthInputWrapper>

            <Button.CTA type="submit">Submit your feedback</Button.CTA>
        </Column>
    )
}

const RequestMerchantContent: React.VFC = () => {
    return (
        <Column align="center">
            <EmojiLogo src={magnify} />
            <Spacer height={16} />
            <Body.SmBold>Request a Bill</Body.SmBold>

            <Spacer height={16} />
            <Body.Med size={14}>
                Let us know the bill name, and any other relevant details, and we'll look into it!
            </Body.Med>
            <Spacer height={32} />
            <FullWidthInputWrapper>
                <Form.Input name="merchant_name" placeholder="Name of Bill" />
                <Form.Input textarea name="feedback_text" placeholder="Any other details" />
            </FullWidthInputWrapper>

            <Button.CTA type="submit">Send Request</Button.CTA>
        </Column>
    )
}

const EmailFeedbackContent = () => {
    return (
        <Column align="center">
            <EmojiLogo src={confusedface} />
            <Spacer height={16} />
            <Body.SmBold>This isn't helpful</Body.SmBold>
            <Spacer height={16} />
            <Body.Med size={14}>
                We're sorry to hear that! Please, help us understand how to make these emails more
                helpful for you. We read every suggestion!
            </Body.Med>
            <Spacer height={32} />
            <FullWidthInputWrapper>
                <Form.Input
                    textarea
                    name="feedback_text"
                    placeholder="Type your feedback here"
                    validation={{ required: true }}
                />
            </FullWidthInputWrapper>

            <Button.CTA type="submit">Submit your feedback</Button.CTA>
        </Column>
    )
}

const Logo = styled.img`
    width: 22px;
    position: absolute;
    top: -2px;
    right: -25px;
`
const InfoWrapper = styled.div`
    width: 100%;
    background: var(--surface-3, rgba(231, 250, 244, 1));
`
const WalletContent = () => {
    return (
        <Column align="center">
            <Spacer height={8} />
            <Body.Bold size={18}>Problems connecting to a wallet</Body.Bold>
            <Spacer height={32} />

            <InfoWrapper>
                <Row style={{ padding: 16 }}>
                    <Column>
                        <Body.Bold size={14}>Need a refresher?</Body.Bold>
                        <Spacer height={8} />
                        <Row style={{ flexWrap: 'wrap', display: 'inline' }}>
                            <Body.Med size={14}>You can use</Body.Med>
                            <Body.SmBold
                                size={14}
                                style={{
                                    position: 'relative',
                                    marginLeft: '4px',
                                    marginRight: '30px',
                                }}
                            >
                                WalletConnect <Logo src={walletConnect} alt="Wallet Connect Logo" />
                            </Body.SmBold>
                            <Body.Med size={14}>
                                to connect to popular wallet from any device, including
                            </Body.Med>
                            <Body.SmBold
                                size={14}
                                style={{
                                    position: 'relative',
                                    marginLeft: '4px',
                                    marginRight: '30px',
                                }}
                            >
                                MetaMask <Logo src={metamaskLogo} alt="MetaMask Logo" />
                            </Body.SmBold>
                        </Row>
                    </Column>
                </Row>
            </InfoWrapper>
            <Spacer height={24} />
            <FullWidthInputWrapper>
                <Form.Input
                    name="wallet_name"
                    placeholder="Which wallet are you trying to connect?"
                />
                <Form.Input
                    textarea
                    name="feedback_text"
                    placeholder="Please tell us about your issue"
                />
            </FullWidthInputWrapper>
            <Spacer height={32} />
            <Button.CTA type="submit">Report problem</Button.CTA>
        </Column>
    )
}

const NoPaymentContent = () => {
    return (
        <Column align="center">
            <EmojiLogo src={evilmonkey} />
            <Spacer height={16} />
            <Body.SmBold>I can't make a payment now</Body.SmBold>
            <Spacer height={16} />
            <Body.Med size={14}>Please, specify why you can't make a payment now</Body.Med>
            <Spacer height={32} />
            <FullWidthInputWrapper>
                <Form.RadioGroup
                    name="reason"
                    validation={{
                        required: true,
                    }}
                    options={[
                        {
                            id: 'no-funds',
                            value: "I don't have enough funds",
                        },
                        {
                            id: 'not-due-now',
                            value: 'My bill is not due now',
                        },
                        {
                            id: 'another-issue',
                            value: "I'm having another issue",
                        },
                    ]}
                    optionValue={(answer) => answer.id}
                    renderLabel={(answer) => answer.value}
                />
                <Form.Input
                    textarea
                    name="feedback_text"
                    placeholder="Is there anything else you'd like us to know?"
                />
            </FullWidthInputWrapper>

            <Button.CTA type="submit">Submit your feedback</Button.CTA>
        </Column>
    )
}

type FeedbackFormProps = {
    formId: FeedbackForm
    label?: string
    onSubmitForm?: () => void
}

const getEmail = () => {
    try {
        const { email } = JSON.parse(localStorage.getItem('ajs_user_traits')!)
        return email
    } catch (err) {
        return null
    }
}

const useSendFeedback = () => {
    const email = getEmail()
    const send = async (data: any, formId: FeedbackForm) => {
        const url = config.graphqlEndpoint.replace('/graphql', '/users/feedback')
        await axios.post(url, {
            ...data,
            formId,
            email,
        })
    }

    return send
}

export const GenericFeedbackForm: React.VFC<FeedbackFormProps> = ({
    formId,
    label,
    onSubmitForm,
}) => {
    const [submitted, setSubmitted] = useState(false)
    const { trackAnalyticsEvent } = useAnalytics()
    const sendFeedback = useSendFeedback()

    const onSubmit: SubmitHandler<any> = (data) => {
        sendFeedback(data, formId)
        setSubmitted(true)
        trackAnalyticsEvent(FEEDBACK_SUBMITTED, {
            formId,
            ...data,
        })
    }

    let content,
        initialValues = {}

    switch (formId) {
        case FeedbackForm.GenericFeedback:
            content = <GenericFeedbackContent />
            break
        case FeedbackForm.EmailNotHelpful:
            content = <EmailFeedbackContent />
            break
        case FeedbackForm.NoPayment:
            content = <NoPaymentContent />
            break
        case FeedbackForm.RequestMerchant:
            initialValues = { merchant_name: label }
            content = <RequestMerchantContent />
            break
        case FeedbackForm.Wallet:
            content = <WalletContent />
            break
    }

    const handleClose = () => {
        setSubmitted(false)
        onSubmitForm?.()
    }

    return (
        <FadePresence renderKey={submitted}>
            {submitted ? (
                <ThankYou handleClose={handleClose} />
            ) : (
                <Form onSubmit={onSubmit} width="100%" initialValues={initialValues}>
                    {content}
                </Form>
            )}
        </FadePresence>
    )
}
